import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Col, Container, Row, CardBody, Card, Button } from 'reactstrap';
import { useHistory, useParams } from 'react-router-dom';
import { getFormValues, isValid, touch } from 'redux-form';
import { connect } from 'react-redux';
import moment from 'moment';
import Api from '../../util/api';
import Loading from '../../shared/components/custom/Loading';
import t, { partial } from '../../util/translation/translation';
import SpotlightTileForm from './components/SpotlightTileForm';
import TileAccessForm from './components/TileAccessForm';
import { convertToBoolean, isFilterable } from './spotlightUtils';
import SpotlightPhonePreview from './components/SpotlightPhonePreview';
import SpotlightStoryPreview from './components/SpotlightStoryPreview';
import SpotlightTakeoverPreview from './components/SpotlightTakeoverPreview';
import SpotlightTakeoverGenericPreview from './components/SpotlightTakeoverGenericPreview';
import { isGenericTakeover, presentationFromForm } from './takeoverUtils';
import FormCard from '../../shared/components/custom/FormCard';

const SpotlightTileFormPage = ({
  tileAccessFormValues,
  tileFormValues,
  tileAccessFormValid,
  tileFormValid,
  dispatch,
}) => {
  const history = useHistory();
  const { groupID, tileID: entityID } = useParams();
  const [entity, setEntity] = useState(null);
  const [entityLoaded, setEntityLoaded] = useState(!entityID);
  const [loading, setLoading] = useState(false);
  const [tileFormLoading, setTileFormLoading] = useState(false);
  const [groupData, setGroupData] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [loadingGroupData, setLoadingGroupData] = useState(false);
  const [selectedEntityAccess, setSelectedEntityAccess] = useState(null);
  const [tileAccess, setAccess] = useState({});
  const [segments, setSegments] = useState([]);
  // Story preview: as someone without access sees a members-only slide
  const [previewLocked, setPreviewLocked] = useState(false);
  const [groupsLevelsTiers, setGroupsLevelsTiers] = useState({
    tiers: [],
    groups: [],
    levels: [],
  });
  const p = partial('HighlightBannerFormPage');
  const s = partial('shared');
  const e = partial('EventsForm');
  const getData = async () => {
    setEntityLoaded(false);
    setLoading(true);
    try {
      const tileData = await Api.spotlight.getTileById(entityID);
      setEntity(tileData);
      setLoading(false);
      setEntityLoaded(true);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoading(false);
      setEntityLoaded(true);
    }
  };
  const getTileAccess = async () => {
    setLoadingAccess(true);
    try {
      const [resAccess, resLevels, resGroups, { data: resTiers }, resSegments] =
        await Promise.all([
          Api.spotlight.getTileAccess(entityID),
          Api.club.getLevels(),
          Api.club.getGroups(),
          Api.tiers.allTiers(),
          Api.spotlight.getSegments(),
        ]);
      const mappedTiers = [
        { label: e('allTiers'), value: [] },
        { label: s('ignore'), value: null },
        ...resTiers.map((item) => ({ label: item.name, value: item.id })),
      ];
      const mappedGroups = [
        { label: e('allGroups'), value: [] },
        { label: s('ignore'), value: null },
        ...resGroups.map((item) => ({ label: item.value, value: item.value })),
      ];
      const mappedLevels = [
        { label: e('allLevels'), value: [] },
        { label: s('ignore'), value: null },
        ...resLevels.map((item) => ({ label: item.value, value: item.value })),
      ];
      // As when creating: "ignore" is the form's value for no segments
      const mappedSegments = [
        { label: s('ignore'), value: null },
        ...resSegments.map((item) => ({ label: item.name, value: item.id })),
      ];
      setGroupsLevelsTiers({
        tiers: mappedTiers,
        groups: mappedGroups,
        levels: mappedLevels,
      });
      setSegments(mappedSegments);
      setLoadingAccess(false);
      setAccess(resAccess);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoadingAccess(false);
    }
  };
  const getGroupsTiersLevels = async () => {
    setLoadingAccess(true);
    try {
      const [resLevels, resGroups, { data: resTiers }, resSegments] =
        await Promise.all([
          Api.club.getLevels(),
          Api.club.getGroups(),
          Api.tiers.allTiers(),
          Api.spotlight.getSegments(),
        ]);
      const mappedTiers = [
        { label: e('allTiers'), value: [] },
        { label: s('ignore'), value: null },
        ...resTiers.map((item) => ({ label: item.name, value: item.id })),
      ];
      const mappedGroups = [
        { label: e('allGroups'), value: [] },
        { label: s('ignore'), value: null },
        ...resGroups.map((item) => ({ label: item.value, value: item.value })),
      ];
      const mappedLevels = [
        { label: e('allLevels'), value: [] },
        { label: s('ignore'), value: null },
        ...resLevels.map((item) => ({ label: item.value, value: item.value })),
      ];
      const mappedSegments = [
        { label: s('ignore'), value: null },
        ...resSegments.map((item) => ({ label: item.name, value: item.id })),
      ];
      setSegments(mappedSegments);
      setGroupsLevelsTiers({
        tiers: mappedTiers,
        groups: mappedGroups,
        levels: mappedLevels,
      });
      setLoadingAccess(false);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoadingAccess(false);
    }
  };
  const getGroupData = async () => {
    setLoadingGroupData(true);
    try {
      const groupDataResponse = await Api.spotlight.getGroupById(groupID);
      setGroupData(groupDataResponse);
      setLoadingGroupData(false);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoadingGroupData(false);
    }
  };
  useEffect(() => {
    if (entityID) {
      getData();
      // getTileAccess already fetches levels/groups/tiers/segments alongside
      // the tile's access, so getGroupsTiersLevels is only needed when
      // there's no existing tile access to load (create flow). Calling both
      // here raced them against each other and made which segments mapping
      // "won" (and when the loading spinner cleared) non-deterministic.
      getTileAccess();
    } else {
      setEntityLoaded(true);
      getGroupsTiersLevels();
    }
    getGroupData();
  }, [entityID]);
  const getSubmitContentId = (values) => {
    // Image tiles have no target
    if (values.content.value === 'IMAGE') {
      return null;
    }
    if (values.content.value === 'AUCTIONS') {
      const auctionHouseId = values.contentType.value;
      const subContentType = values?.subContentType?.value;
      return auctionHouseId
        ? `${auctionHouseId}${subContentType ? `|${subContentType}` : ''}`
        : null;
    }
    if (isFilterable(values.content.value)) {
      return values.contentType.value;
    } else {
      return values.contentType;
    }
  };
  const onTileSubmit = async (values) => {
    // The image-and-text takeover: its description is the text, and it has
    // no number in the light
    const genericTakeover =
      groupData?.format === 'TAKEOVER' &&
      isGenericTakeover(values.presentation);
    const payload = {
      title: values.title,
      description: genericTakeover
        ? `${values.description || ''}`.trim() || null
        : values.description,
      publicationDate: moment(values.publicationDate),
      expirationDate: values.expirationDate
        ? moment(values.expirationDate)
        : undefined,
      contentType: values.content.value,
      contentId: getSubmitContentId(values),
      companyId: values?.company?.value || null,
      badge: values?.badge?.value || null,
      // Story slides: the big figure
      highlight: genericTakeover ? null : values?.highlight?.trim() || null,
      groupId: groupID,
      image:
        values?.headingImage?.croppedBase64 || values?.headingImage?.preview,
      // A takeover's own text and style (null clears it); only sent for
      // takeovers
      presentation:
        groupData?.format === 'TAKEOVER'
          ? presentationFromForm(values.presentation)
          : undefined,
    };

    setLoading(true);
    let finalTileId = null;
    try {
      if (values.content.value === 'CUSTOM_VIDEO') {
        if (values.headingVideo.promise) {
          const publicUrl = await values.headingVideo.promise();
          payload.contentId = publicUrl;
        } else {
          payload.contentId = values.headingVideo?.preview;
        }
      }
      if (entityID) {
        await Api.spotlight.updateTile(entityID, payload);
      } else {
        const createTileResponse = await Api.spotlight.createTile(payload);
        finalTileId = createTileResponse.id;
      }
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoading(false);
      return null;
    }
    setLoading(false);

    return { tileId: entityID || finalTileId, isNew: !!finalTileId };
  };
  const getAccessValues = (searchArray) => {
    if (searchArray.length === 0) {
      return null;
    }
    const foundItem = searchArray.find(
      (item) => Array.isArray(item.value) || item.value === null
    );
    if (foundItem) {
      return foundItem.value;
    }
    return searchArray.map((item) => item.value);
  };
  const onAccessSubmit = async (tileId, values) => {
    const payload = {
      tiers: getAccessValues(values?.tier || []),
      groups: getAccessValues(values?.group || []),
      levels: getAccessValues(values?.level || []),
      noAccessView: convertToBoolean(values?.noAccessView) || false,
      segmentIds: getAccessValues(values?.segments || []) || [],
      visible: true,
    };
    setLoadingAccess(true);
    try {
      await Api.spotlight.updateTileAccess(tileId, payload);
      history.goBack();
      toast.success(p('tileUpdated'));
    } catch (err) {
      toast.error(p('updatingAccessFailed'));
    }
    setLoadingAccess(false);
  };

  const pollTileAccess = async (tileId, callBack, iteration = 0) => {
    if (iteration > 10) {
      // The access request was never sent - stay on the page so the
      // access settings the user configured aren't silently discarded.
      toast.error(p('updatingAccessFailed'));
      setLoadingAccess(false);
      return;
    }
    try {
      await Api.spotlight.getTileAccess(tileId);
      callBack();
    } catch (err) {
      setTimeout(() => pollTileAccess(tileId, callBack, iteration + 1), 1000);
    }
  };

  const handleTileSubmit = async () => {
    if (!tileFormValid) {
      dispatch(
        touch(
          'tile_form',
          'title',
          'headingImage',
          'publicationDate',
          'expirationDate',
          'description',
          'content',
          'contentType',
          'subContentType',
          'headingVideo',
          'presentation.eyebrow',
          'presentation.firstLine',
          'presentation.facts[0]',
          'presentation.facts[1]',
          'presentation.facts[2]',
          'presentation.actionLabel',
          'presentation.moreLabel',
          'presentation.moreLink',
          'presentation.lightColor'
        )
      );
      return;
    }
    if (!tileAccessFormValid) {
      dispatch(touch('tile_access_form', 'level', 'group', 'tier'));
      return;
    }
    const tileData = await onTileSubmit(tileFormValues);
    if (!tileData) {
      return;
    }
    if (tileData.isNew) {
      setLoadingAccess(true);
      pollTileAccess(tileData.tileId, () =>
        onAccessSubmit(tileData.tileId, tileAccessFormValues)
      );
    } else {
      await onAccessSubmit(tileData.tileId, tileAccessFormValues);
    }
  };
  const entityLoading = !!entityID && !entityLoaded;
  // Members-only (a chosen audience), and whether everyone else sees it
  // locked rather than not at all (the app's exclusive label and lock)
  const storyExclusive =
    tileAccessFormValues?.targetAudience === 'targetAudience';
  const storyLockable = tileAccessFormValues?.noAccessView === 'true';

  return (
    <Container>
      <Loading
        loading={
          loading ||
          loadingAccess ||
          loadingGroupData ||
          entityLoading ||
          tileFormLoading
        }
      />
      <Row md={12}>
        <Col md={8}>
          <Card>
            <CardBody>
              <Row>
                <Col>
                  <h3 className="page-title">
                    {entityID ? t('shared.edit') : t('shared.create')}{' '}
                    {s('tile')}
                  </h3>
                </Col>
              </Row>
              <SpotlightTileForm
                setSelectedEntityAccess={setSelectedEntityAccess}
                entity={entity}
                size={groupData?.size}
                format={groupData?.format}
                descriptionAvailable={groupData?.descriptionAvailable}
                groupsLevelsTiers={groupsLevelsTiers}
                onLoadingChange={setTileFormLoading}
              />

              {!entityLoading &&
                tileFormValues?.content?.value !== 'GOAL_SPONSOR' && (
                  <TileAccessForm
                    selectedEntityAccess={selectedEntityAccess}
                    tile={entity}
                    access={tileAccess}
                    segments={segments}
                    groupsLevelsTiers={groupsLevelsTiers}
                  />
                )}
              {!entityLoading && (
                <Col md={12}>
                  <Button
                    color="primary"
                    onClick={handleTileSubmit}
                    disabled={loading || loadingAccess}
                  >
                    {entityID ? s('save') : s('create')}
                  </Button>
                  <Button type="button" onClick={() => history.goBack()}>
                    {s('close')}
                  </Button>
                </Col>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col md={4}>
          <FormCard title={t('NewsForm.appPreview')} className="mb-3">
            {groupData?.format === 'TAKEOVER' ? (
              // A takeover: the full-screen presentation the app opens
              isGenericTakeover(tileFormValues?.presentation) ? (
                <SpotlightTakeoverGenericPreview tile={tileFormValues} />
              ) : (
                <SpotlightTakeoverPreview tile={tileFormValues} />
              )
            ) : groupData?.format === 'STORY' ? (
              // A story's tiles are full-screen slides; a members-only one
              // shows the exclusive label, and a locked view for others when
              // they see it locked
              <>
                {storyExclusive && storyLockable && (
                  <div className="story-preview__as" role="group">
                    {[false, true].map((asLocked) => (
                      <button
                        key={String(asLocked)}
                        type="button"
                        className={`story-preview__as-btn${
                          previewLocked === asLocked
                            ? ' story-preview__as-btn--on'
                            : ''
                        }`}
                        aria-pressed={previewLocked === asLocked}
                        onClick={() => setPreviewLocked(asLocked)}
                      >
                        {t(
                          `HighlightBannerForm.${
                            asLocked ? 'storyAsLocked' : 'storyAsMember'
                          }`
                        )}
                      </button>
                    ))}
                  </div>
                )}
                <SpotlightStoryPreview
                  group={groupData}
                  tile={tileFormValues}
                  exclusive={storyExclusive}
                  locked={storyExclusive && storyLockable && previewLocked}
                />
              </>
            ) : tileFormValues?.headingImage?.preview ||
              groupData?.format === 'TEXT_BANNER' ||
              tileFormValues?.content?.value === 'YOUTUBE_VIDEO' ? (
              <SpotlightPhonePreview
                group={groupData}
                tiles={[
                  {
                    key: 'tile',
                    tile: tileFormValues || {},
                    access: tileAccessFormValues,
                  },
                ]}
                ghost
              />
            ) : (
              <SpotlightPhonePreview
                group={groupData}
                tiles={[]}
                emptyText={t('HighlightBannerPage.previewNoImage')}
              />
            )}
          </FormCard>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  tileAccessFormValues: getFormValues('tile_access_form')(state),
  tileFormValues: getFormValues('tile_form')(state),
  tileAccessFormValid: isValid('tile_access_form')(state),
  tileFormValid: isValid('tile_form')(state),
});
export default connect(mapStateToProps)(SpotlightTileFormPage);
